import { hasMethods, isFunction, isObject, isString } from "@videojs/utils/predicate";
//#region src/engine.ts
/**
* Pick the `mux-embed` integration for a media's playback engine.
*
* `mux-embed` monitors the media element on its own, and an engine integration is what adds engine-level data on top:
* rendition switches, request timing and throughput, and engine errors. It ships two — hls.js and dash.js — and each
* one is wired through a different option, so handing a dash.js player to the hls.js option leaves the view with
* element-level data only.
*
* Engines are matched by shape rather than by class so this module imports neither hls.js nor dash.js. Mux Data can be
* registered with any media without pulling an engine it will never touch into the bundle (dash.js in particular reads
* `window` on import), and a media whose engine has no integration — a raw `<video>`, native HLS, or an SPF playback
* engine — is monitored from the element alone instead of through the wrong integration.
*
* @returns Options to spread into a `Mux.monitor()` call. Empty when the engine has no integration, which leaves
*   element-level monitoring intact.
* @internal
*/
function toMuxDataEngineOptions(engine) {
	if (isDashJsEngine(engine)) return { dashjs: engine };
	if (isHlsJsEngine(engine)) {
		const Hls = toHlsJsClass(engine);
		if (Hls) return {
			hlsjs: engine,
			Hls
		};
	}
	return {};
}
/** Hls.js: its monitor reads renditions from `levels`. */
function isHlsJsEngine(engine) {
	return hasMethods(engine, ["on", "off"]) && Array.isArray(engine.levels);
}
/** Dash.js: its monitor reads renditions through the track and rendition-list getters. */
function isDashJsEngine(engine) {
	if (!hasMethods(engine, [
		"on",
		"off",
		"getCurrentTrackFor"
	])) return false;
	return hasMethods(engine, ["getRepresentationsByType"]) || hasMethods(engine, ["getBitrateInfoListFor"]);
}
/** Hls.js's own class, the only place its event names and error details are published. */
function toHlsJsClass(engine) {
	const engineClass = engine.constructor;
	if (!isFunction(engineClass)) return void 0;
	const statics = engineClass;
	return isObject(statics.Events) && isObject(statics.ErrorDetails) && isString(statics.version) ? statics : void 0;
}
//#endregion
export { toMuxDataEngineOptions };
